import React, { useCallback, useEffect } from "react";
import { HighlightOffOutlined } from "@mui/icons-material";
import {
  Autocomplete as AutocompleteMui,
  AutocompleteValue,
  InputAdornment,
  FilterOptionsState,
  Chip,
  styled,
} from "@mui/material";
import { TextField } from "../TextField";
import { AutoCompleteProps, AutoCompleteOption } from "./types";
import useFilterOptions from "./useFilterOptions";

const AutoComplete = ({
  onSelect,
  options,
  icon,
  iconPosition,
  placeHolder,
  className,
  useChips,
  defaultValue,
  readOnly,
  selectedValues,
  createOptionLabel,
  maxResults = 200,
}: AutoCompleteProps) => {
  const { query, setQuery, filteredOptions } = useFilterOptions({
    options,
    maxResults,
  });

  const onChange = (
    event: React.SyntheticEvent,
    value: AutocompleteValue<AutoCompleteOption, boolean, boolean, boolean>
  ): void => {
    const formatedValue =
      typeof value === "object" ? (value as AutoCompleteOption) : undefined;

    if (formatedValue && formatedValue.id) {
      setQuery(formatedValue.label);
      return onSelect(formatedValue, formatedValue.isNewOption);
    }
    setQuery("");
    return onSelect(undefined);
  };

  const filterOptions = useCallback(
    (
      options: AutoCompleteOption[],
      { inputValue }: FilterOptionsState<AutoCompleteOption>
    ) => {
      const formatedInputValue = inputValue.replace("*", "");

      if (!inputValue && (!query || query === "")) {
        return options.slice(0, maxResults);
      }
      if (useChips && !readOnly && formatedInputValue) {
        return [
          ...filteredOptions,
          {
            id: formatedInputValue,
            label: `${createOptionLabel} ${formatedInputValue}`,
            isNewOption: true,
          },
        ];
      }

      return filteredOptions;
    },
    [filteredOptions, createOptionLabel, readOnly]
  );
  const alignTo = iconPosition ?? "end";
  useEffect(() => {
    setQuery(
      Array.isArray(selectedValues)
        ? selectedValues.join(", ")
        : selectedValues?.label ?? ""
    );
  }, [selectedValues]);

  return (
    <AutocompleteMui
      multiple={useChips || Array.isArray(selectedValues)}
      onChange={onChange}
      freeSolo
      readOnly={readOnly}
      className={className}
      options={options}
      getOptionKey={(option) =>
        typeof option === "string" ? option : option.id
      }
      getOptionLabel={(option) => {
        return typeof option === "string"
          ? option
          : (option as AutoCompleteOption).label;
      }}
      renderTags={
        useChips
          ? (values, getTagProps) =>
              values.map((value, index) => (
                <StyledChip
                  variant="filled"
                  label={
                    value.isNewOption && createOptionLabel
                      ? value.label.replace(createOptionLabel, "")
                      : value.label
                  }
                  {...getTagProps({ index })}
                  deleteIcon={<HighlightOffOutlined />}
                />
              ))
          : undefined
      }
      value={selectedValues ?? defaultValue}
      filterOptions={filterOptions}
      defaultValue={useChips ? selectedValues : undefined}
      disablePortal
      {...(defaultValue && { defaultValue })}
      renderInput={(params) => {
        const paramsFixedValue = {
          ...params,
          inputProps: {
            ...params.inputProps,
            value: query,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              if (params.inputProps.onChange) {
                params.inputProps.onChange(e);
              }
              setQuery(e.target.value);
            },
          },
        };

        return (
          <TextField
            {...paramsFixedValue}
            fullWidth
            placeholder={!readOnly ? placeHolder ?? "search" : ""}
            InputProps={{
              ...params.InputProps,
              [`${alignTo}Adornment`]: icon ? (
                <InputAdornment
                  position={alignTo}
                  sx={
                    alignTo === "end"
                      ? { marginRight: "8px" }
                      : { marginLeft: "8px" }
                  }
                >
                  {icon}
                </InputAdornment>
              ) : (
                params.InputProps[`${alignTo}Adornment`]
              ),
            }}
          />
        );
      }}
    />
  );
};
export default AutoComplete;
const StyledChip = styled(Chip)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,

  display: "flex",
  alignItems: "center",
  backgroundColor: "#333333",
  span: { color: theme.tokens.colors.gray[7] },

  svg: {
    color: theme.tokens.colors.gray[7],
  },
}));
